import { Controller } from "@hotwired/stimulus"
import { useDebounce } from "stimulus-use"

// Contrôleur pour gérer la prévisualisation dans la modale de création/édition de rendez-vous
export default class extends Controller {
  static debounces = ["updatePreview"]
  static values = { 
    bookableId: String,
    bookableType: String,
    slotTime: String
  }
  static targets = [
    "bookableIdField", 
    "startTimeField", 
    "startTimeOnlyField", 
    "warningContainer",
    "form"
  ]

  connect() {
    useDebounce(this, { wait: 300 })
    
    console.log(`TimeslotReservationModal connecting for slot ${this.bookableIdValue}_${this.slotTimeValue}`)
    
    // Trouver le contrôleur parent de réservation de créneaux
    this.reservationController = this.getReservationController()
    
    if (this.reservationController) {
      console.log("Reservation controller found, checking existing preview")
      
      // Vérifier si la date du formulaire correspond à celle de l'agenda
      const formDate = this.getFormDate()
      const agendaDate = this.getAgendaDate()
      
      if (formDate && agendaDate && formDate !== agendaDate) {
        console.log(`Form date (${formDate}) doesn't match agenda date (${agendaDate}), not creating preview`)
      } else {
        // Vérifier s'il y a déjà une preview pour ce même créneau
        const existingPreview = this.reservationController.currentPreview
        const isSameSlot = existingPreview && 
                          existingPreview.bookableId === this.bookableIdValue && 
                          existingPreview.slotTime === this.slotTimeValue
        
        if (isSameSlot) {
          console.log("Preview already exists for this slot, not creating a new one")
        } else {
          console.log("Starting preview for new slot")
          // Démarrer la prévisualisation pour ce créneau avec la durée du formulaire
          const duration = this.getFormDuration()
          this.reservationController.startPreview(this.bookableIdValue, this.slotTimeValue, duration)
        }
      }
    } else {
      console.warn("No reservation controller found during connect")
    }

    // Écouter l'événement de fermeture de la modale
    this.element.addEventListener('dialog:before-close', this.handleModalClose.bind(this))

    console.log(`TimeslotReservationModal connected for slot ${this.bookableIdValue}_${this.slotTimeValue}`)
  }

  disconnect() {
    // Arrêter la prévisualisation quand la modale se ferme
    if (this.reservationController) {
      this.reservationController.stopPreview()
    }

    // Nettoyer l'écouteur d'événement
    this.element.removeEventListener('dialog:before-close', this.handleModalClose.bind(this))
  }

  // Gestionnaire pour la fermeture de modale
  handleModalClose(event) {
    console.log("Modal is closing, cleaning up preview")
    
    // Arrêter la prévisualisation
    if (this.reservationController) {
      this.reservationController.stopPreview()
    }
  }

  // Méthode appelée quand l'utilisateur change les paramètres dans la modale
  // (par exemple changement d'heure ou de ressource)
  updatePreview() {
    if (!this.reservationController) {
      console.warn("No reservation controller found, cannot update preview")
      return
    }

    // Lire les nouvelles valeurs depuis le formulaire
    const newBookableId = this.getFormBookableId()
    const newSlotTime = this.getFormSlotTime()
    const newDuration = this.getFormDuration()
    const newDate = this.getFormDate()

    console.log(`Updating preview: bookable=${newBookableId}, slot=${newSlotTime}, duration=${newDuration}, date=${newDate}`)

    // Vérifier si la date du formulaire correspond à la date de l'agenda
    const agendaDate = this.getAgendaDate()
    if (newDate && agendaDate && newDate !== agendaDate) {
      console.log(`Form date (${newDate}) doesn't match agenda date (${agendaDate}), stopping preview`)
      this.reservationController.stopPreview()
      this.hideSlotUnavailableWarning()
      return
    }

    if (newBookableId && newSlotTime) {
      // Mettre à jour la prévisualisation avec la nouvelle durée
      const success = this.reservationController.updatePreview(newBookableId, newSlotTime, newDuration)
      
      if (!success) {
        // Le créneau est occupé, montrer un message d'avertissement
        this.showSlotUnavailableWarning()
      } else {
        this.hideSlotUnavailableWarning()
        
        // Mettre à jour les valeurs du contrôleur
        this.bookableIdValue = newBookableId
        this.slotTimeValue = newSlotTime
      }
    }
  }

  // Méthode appelée quand le formulaire est soumis avec succès
  onFormSuccess() {
    console.log("Form submitted successfully, stopping preview immediately")
    
    // Arrêter immédiatement la prévisualisation côté client
    if (this.reservationController) {
      this.reservationController.stopPreview()
    }
    
    // Note: La prévisualisation sera aussi nettoyée côté serveur
    // par la méthode clean_preview_for_appointment dans CreateAppointment
  }

  // Gestionnaire pour l'événement turbo:submit-end
  handleFormSubmit(event) {
    console.log("Form submit event:", event)
    console.log("Event detail:", event.detail)
    
    // Pour Turbo, on peut vérifier le succès de plusieurs façons :
    // 1. Vérifier si l'événement est un succès (pas d'erreur)
    // 2. Vérifier le statut de la réponse dans event.detail
    
    const detail = event.detail
    
    // Méthode 1: Vérifier la présence d'une erreur
    if (!detail.error && !detail.fetchResponse?.failed) {
      console.log("Form submitted successfully (no error), calling onFormSuccess")
      this.onFormSuccess()
      return
    }
    
    // Méthode 2: Vérifier le statut HTTP de la réponse
    if (detail.fetchResponse?.response?.ok) {
      console.log("Form submitted successfully (response ok), calling onFormSuccess")
      this.onFormSuccess()
      return
    }
    
    // Méthode 3: Fallback - si pas d'erreur explicite, considérer comme succès
    if (!detail.error) {
      console.log("Form submitted successfully (fallback), calling onFormSuccess")
      this.onFormSuccess()
      return
    }
    
    console.log("Form submission failed or uncertain:", detail)
  }

  // Méthode pour forcer l'arrêt de la prévisualisation (utilisée par stimulus-components lors de la fermeture de modale)
  forceStopPreview() {
    if (this.reservationController) {
      this.reservationController.stopPreview()
    }
  }

  // Trouver le contrôleur parent de réservation
  getReservationController() {
    // Méthode 1: Chercher dans les parents DOM pour trouver l'élément avec le contrôleur timeslot-reservation
    let element = this.element.parentElement
    
    while (element && element !== document.body) {
      const controller = this.application.getControllerForElementAndIdentifier(element, "timeslot-reservation")
      if (controller) {
        console.log("Found timeslot-reservation controller via DOM traversal")
        return controller
      }
      element = element.parentElement
    }

    // Méthode 2: Chercher dans tout le document (en cas de modal détachée)
    const scheduleElement = document.querySelector('[data-controller*="timeslot-reservation"]')
    if (scheduleElement) {
      const controller = this.application.getControllerForElementAndIdentifier(scheduleElement, "timeslot-reservation")
      if (controller) {
        console.log("Found timeslot-reservation controller via document query")
        return controller
      }
    }

    // Méthode 3: Utiliser l'API Stimulus pour chercher tous les contrôleurs actifs
    try {
      const allControllers = this.application.controllers
      const reservationController = allControllers.find(ctrl => 
        ctrl.identifier === "timeslot-reservation"
      )
      if (reservationController) {
        console.log("Found timeslot-reservation controller via application controllers")
        return reservationController
      }
    } catch (error) {
      console.warn("Error searching controllers:", error)
    }

    console.warn("Could not find parent timeslot-reservation controller")
    return null
  }

  // Extraire l'ID de la ressource depuis le formulaire
  getFormBookableId() {
    // Utiliser le target si disponible, sinon fallback sur querySelector
    if (this.hasBookableIdFieldTarget) {
      return this.bookableIdFieldTarget.value
    }
    
    const bookableIdField = this.element.querySelector('select[name*="bookable_id"], input[name*="bookable_id"]')
    return bookableIdField ? bookableIdField.value : this.bookableIdValue
  }

  // Extraire le temps du créneau depuis le formulaire
  getFormSlotTime() {
    // Alternative : chercher des champs séparés pour date et heure
    if (this.hasStartTimeOnlyFieldTarget && this.startTimeOnlyFieldTarget.value) {
      try {
        const timeValue = this.startTimeOnlyFieldTarget.value
        console.log(`Parsing time field value: '${timeValue}'`)
        
        // L'input type="time" retourne toujours au format HH:MM (24h) même si l'affichage est en 12h
        const [hoursStr, minutesStr] = timeValue.split(':')
        const hours = parseInt(hoursStr, 10)
        const minutes = parseInt(minutesStr, 10)
        
        // Valider les valeurs
        if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
          console.warn(`Invalid time values: hours=${hours}, minutes=${minutes}`)
          return this.slotTimeValue
        }
        
        const formattedTime = `${hours.toString().padStart(2, '0')}${minutes.toString().padStart(2, '0')}`
        console.log(`Formatted time: ${formattedTime} (from hours=${hours}, minutes=${minutes})`)
        return formattedTime
      } catch (error) {
        console.warn("Could not parse time only field:", error)
      }
    }

    // Chercher le champ de temps ou construire depuis les champs de date/heure
    if (this.hasStartTimeFieldTarget && this.startTimeFieldTarget.value) {
      try {
        const dateValue = this.startTimeFieldTarget.value
        console.log(`Parsing date field value: '${dateValue}'`)
        
        const dateTime = new Date(dateValue)
        if (isNaN(dateTime.getTime())) {
          console.warn(`Invalid date: ${dateValue}`)
          return this.slotTimeValue
        }
        
        const hours = dateTime.getHours()
        const minutes = dateTime.getMinutes()
        const formattedTime = `${hours.toString().padStart(2, '0')}${minutes.toString().padStart(2, '0')}`
        console.log(`Formatted time from date: ${formattedTime} (from hours=${hours}, minutes=${minutes})`)
        return formattedTime
      } catch (error) {
        console.warn("Could not parse start time:", error)
      }
    }

    // Fallback sur querySelector si les targets ne sont pas disponibles
    const timeOnlyField = this.element.querySelector('input[name*="start_time_only"]')
    if (timeOnlyField && timeOnlyField.value) {
      try {
        const timeValue = timeOnlyField.value
        console.log(`Parsing fallback time field value: '${timeValue}'`)
        
        const [hoursStr, minutesStr] = timeValue.split(':')
        const hours = parseInt(hoursStr, 10)
        const minutes = parseInt(minutesStr, 10)
        
        if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
          console.warn(`Invalid fallback time values: hours=${hours}, minutes=${minutes}`)
          return this.slotTimeValue
        }
        
        const formattedTime = `${hours.toString().padStart(2, '0')}${minutes.toString().padStart(2, '0')}`
        console.log(`Formatted fallback time: ${formattedTime} (from hours=${hours}, minutes=${minutes})`)
        return formattedTime
      } catch (error) {
        console.warn("Could not parse time only field:", error)
      }
    }

    const startTimeField = this.element.querySelector('input[name*="start_time"]')
    if (startTimeField && startTimeField.value) {
      try {
        const dateValue = startTimeField.value
        console.log(`Parsing fallback date field value: '${dateValue}'`)
        
        const dateTime = new Date(dateValue)
        if (isNaN(dateTime.getTime())) {
          console.warn(`Invalid fallback date: ${dateValue}`)
          return this.slotTimeValue
        }
        
        const hours = dateTime.getHours()
        const minutes = dateTime.getMinutes()
        const formattedTime = `${hours.toString().padStart(2, '0')}${minutes.toString().padStart(2, '0')}`
        console.log(`Formatted fallback time from date: ${formattedTime} (from hours=${hours}, minutes=${minutes})`)
        return formattedTime
      } catch (error) {
        console.warn("Could not parse start time:", error)
      }
    }

    console.log(`Returning default slot time: ${this.slotTimeValue}`)
    return this.slotTimeValue
  }

  // Afficher un avertissement si le créneau n'est pas disponible
  showSlotUnavailableWarning() {
    let warning = this.element.querySelector('.slot-unavailable-warning')
    
    if (!warning) {
      warning = document.createElement('div')
      warning.classList.add('slot-unavailable-warning', 'bg-yellow-50', 'border', 'border-yellow-200', 'rounded-md', 'p-3', 'mb-4')
      warning.innerHTML = `
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-yellow-800">
              Créneau en cours de réservation
            </h3>
            <div class="mt-2 text-sm text-yellow-700">
              <p>Ce créneau est actuellement sélectionné par un autre utilisateur. Veuillez choisir un autre créneau ou attendre.</p>
            </div>
          </div>
        </div>
      `
      
      // Insérer au début du formulaire si le target form existe, sinon utiliser querySelector
      if (this.hasFormTarget && this.formTarget.firstChild) {
        this.formTarget.insertBefore(warning, this.formTarget.firstChild)
      } else {
        const form = this.element.querySelector('form')
        if (form && form.firstChild) {
          form.insertBefore(warning, form.firstChild)
        } else {
          this.element.insertBefore(warning, this.element.firstChild)
        }
      }
    }
    
    warning.style.display = 'block'
  }

  // Cacher l'avertissement
  hideSlotUnavailableWarning() {
    const warning = this.element.querySelector('.slot-unavailable-warning')
    if (warning) {
      warning.style.display = 'none'
    }
  }

  // Actions Stimulus pour les événements de formulaire
  bookableIdChanged() {
    console.log("Bookable ID changed, updating preview")
    this.updatePreview()
  }

  startTimeChanged() {
    console.log("Start time changed, updating preview")
    this.updatePreview()
  }

  startTimeOnlyChanged() {
    console.log("Start time only changed, updating preview")
    this.updatePreview()
  }

  durationChanged() {
    console.log("Duration changed, updating preview")
    this.updatePreview()
  }

  // Extraire la durée depuis le formulaire
  getFormDuration() {
    // Chercher le champ de durée
    const durationField = this.element.querySelector('select[name*="duration_minutes"], input[name*="duration_minutes"]')
    console.log("Duration field found:", durationField)
    console.log("Duration field value:", durationField?.value)
    
    if (durationField && durationField.value) {
      const duration = parseInt(durationField.value, 10)
      if (!isNaN(duration) && duration > 0) {
        console.log(`Found form duration: ${duration} minutes`)
        return duration
      }
    }
    
    // Valeur par défaut si pas trouvée
    console.log("Using default duration: 20 minutes")
    return 20
  }

  // Extraire la date depuis le formulaire
  getFormDate() {
    // Utiliser le target si disponible, sinon fallback sur querySelector
    if (this.hasStartTimeFieldTarget && this.startTimeFieldTarget.value) {
      const dateValue = this.startTimeFieldTarget.value
      console.log(`Getting form date from startTimeField: ${dateValue}`)
      return dateValue
    }
    
    const dateField = this.element.querySelector('input[name*="start_date"]')
    if (dateField && dateField.value) {
      console.log(`Getting form date from date field: ${dateField.value}`)
      return dateField.value
    }
    
    console.log("No form date found")
    return null
  }

  // Obtenir la date de l'agenda affiché
  getAgendaDate() {
    // Chercher le contrôleur schedule qui contient la date de l'agenda
    const scheduleElement = document.querySelector('[data-controller*="schedule"]')
    if (scheduleElement) {
      const scheduleController = this.application.getControllerForElementAndIdentifier(scheduleElement, "schedule")
      if (scheduleController && scheduleController.dateValue) {
        // La date du contrôleur schedule est au format ISO, on extrait juste la partie date
        const agendaDate = scheduleController.dateValue.split('T')[0]
        console.log(`Getting agenda date from schedule controller: ${agendaDate}`)
        return agendaDate
      }
    }
    
    // Fallback : chercher dans les éléments time avec datetime
    const timeElement = document.querySelector('time[datetime]')
    if (timeElement) {
      const datetime = timeElement.getAttribute('datetime')
      if (datetime) {
        const agendaDate = datetime.split('T')[0]
        console.log(`Getting agenda date from time element: ${agendaDate}`)
        return agendaDate
      }
    }
    
    // Méthode 3: Extraire depuis l'URL si elle contient un paramètre de date
    const urlParams = new URLSearchParams(window.location.search)
    const dateParam = urlParams.get('date')
    if (dateParam) {
      console.log(`Getting agenda date from URL param: ${dateParam}`)
      return dateParam
    }
    
    console.log("No agenda date found")
    return null
  }
}
